import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AuthenticationResult,
  type Configuration,
} from '@azure/msal-browser';
import { MICROSOFT_CUSTOMER_CLIENT_ID } from './constants';

// Personal Microsoft accounts only (Outlook / Hotmail / Live). Work or
// school accounts are intentionally out of scope.
const AUTHORITY = 'https://login.microsoftonline.com/consumers';
const SCOPES = ['openid', 'profile', 'email'];

let msalInstance: PublicClientApplication | null = null;
let initPromise: Promise<PublicClientApplication> | null = null;

function getMsalInstance(): Promise<PublicClientApplication> {
  if (msalInstance) return Promise.resolve(msalInstance);
  if (initPromise) return initPromise;
  if (!MICROSOFT_CUSTOMER_CLIENT_ID) {
    return Promise.reject(
      new Error('Microsoft sign-in is not configured (VITE_MICROSOFT_CUSTOMER_CLIENT_ID missing)'),
    );
  }
  const config: Configuration = {
    auth: {
      clientId: MICROSOFT_CUSTOMER_CLIENT_ID,
      authority: AUTHORITY,
      // Cloudflare Pages 308-redirects /<path>.html to /<path>, stripping
      // the extension. MSAL polls popup.location.href for this exact
      // string to know when the popup is done — if it had `.html` here,
      // the polled URL never matches after Cloudflare's redirect and the
      // popup hangs forever on "Signing you in…". Configure the URI as
      // the canonical, post-redirect form. The Azure app registration
      // must register the same URI without the .html extension.
      redirectUri: `${window.location.origin}/auth/microsoft-redirect`,
    },
    cache: {
      // localStorage so MSAL's PKCE / nonce state is reachable from both
      // the parent window and the popup. sessionStorage is per-window
      // and breaks the popup handshake.
      cacheLocation: 'localStorage',
    },
  };
  initPromise = (async () => {
    const app = new PublicClientApplication(config);
    await app.initialize();
    // Drain any leftover interaction state from a previous attempt that
    // was killed mid-flight (browser crash, popup closed by an outer
    // handler, etc). Without this, a fresh loginPopup() on the next
    // click can throw `interaction_in_progress` against stale localStorage.
    await app.handleRedirectPromise().catch(() => {});
    msalInstance = app;
    return app;
  })();
  // Drop the cached promise on failure so a retry can re-init — without
  // this, every subsequent click after a transient init error would re-
  // throw the same rejection until the page is hard-reloaded.
  initPromise.catch(() => {
    initPromise = null;
  });
  return initPromise;
}

// MSAL persists keys like `msal.<clientId>.interaction.status` to the
// configured cache. If a previous popup was abandoned, that flag stays
// "in progress" forever and `loginPopup` rejects with
// `interaction_in_progress` until it's cleared.
function isInteractionInProgress(err: unknown): boolean {
  return (err as { errorCode?: string } | undefined)?.errorCode ===
    'interaction_in_progress';
}

async function clearStaleInteraction(app: PublicClientApplication): Promise<void> {
  // clearCache() drops MSAL's own keys; we also nuke the raw localStorage
  // namespace as a belt-and-braces measure for tokens MSAL didn't track.
  try {
    await app.clearCache();
  } catch {
    // ignore
  }
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('msal.') || key.includes(MICROSOFT_CUSTOMER_CLIENT_ID)) {
      localStorage.removeItem(key);
    }
  }
}

// Returns the Microsoft id_token to forward to the backend. MSAL handles
// nonce + signature verification on the client; the backend re-verifies
// signature, issuer, and audience independently.
export async function signInWithMicrosoft(): Promise<string> {
  const app = await getMsalInstance();
  const popup = () =>
    app.loginPopup({ scopes: SCOPES, prompt: 'select_account' });

  let result: AuthenticationResult;
  try {
    result = await popup();
  } catch (err) {
    if (isInteractionInProgress(err)) {
      // Stale lock from an interrupted attempt — clear it and retry once.
      await clearStaleInteraction(app);
      result = await popup();
    } else if (err instanceof InteractionRequiredAuthError) {
      result = await app.loginPopup({ scopes: SCOPES });
    } else {
      throw err;
    }
  }
  if (!result.idToken) {
    throw new Error('Microsoft sign-in did not return an id token');
  }
  return result.idToken;
}
